import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCcw } from 'lucide-react';
import { Section } from '@/shared/ui/layout/Section';
import { Button } from '@/shared/ui/buttons/Button';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiAdminParcelSetWorkflowStatus } from '@/entities/parcels';
import type { FulfillmentStatus } from '@/shared/types/api/parcels';

type Props = {
  parcelId: string;
  currentStatus: FulfillmentStatus | null | undefined;
};

/**
 * Staff-only inline control to revert a parcel from SORTING back to
 * IN_PROGRESS so the client can re-edit it. Renders only while the parcel is
 * in SORTING — once it's been packaged/shipped, even admin can't reset it
 * (status will follow the party from then on).
 */
export const FeaturesParcelsWorkflowStatusChanger = ({
  parcelId,
  currentStatus,
}: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const revert = useMutate({
    api: () => apiAdminParcelSetWorkflowStatus(parcelId, 'IN_PROGRESS'),
    invalidateKeys: [
      queryKeys.parcels.all,
      queryKeys.parcels.detail(parcelId),
    ],
    onSuccess: () => {
      toast.success(t('parcels.workflowStatus.toast.reverted'));
      setConfirmOpen(false);
    },
    onError: (_e, message) => {
      toast.error(message);
      setConfirmOpen(false);
    },
  });

  // Only show on SORTING. Anything past that — packaged / shipped / arrived —
  // is owned by the party flow and even admin can't reset it from here.
  if (currentStatus !== 'SORTING') return null;

  return (
    <>
      <Section title={t('parcels.workflowStatus.title')}>
        <p className="text-sm text-ink-soft">
          {t('parcels.workflowStatus.cancelHint')}
        </p>
        <div className="mt-3 flex justify-end">
          <Button
            variant="danger"
            leftIcon={<RotateCcw className="h-4 w-4" />}
            loading={revert.isPending}
            onClick={() => setConfirmOpen(true)}
          >
            {t('parcels.workflowStatus.cancelAction')}
          </Button>
        </div>
      </Section>

      <ConfirmDialog
        open={confirmOpen}
        title={t('parcels.workflowStatus.confirmTitle')}
        description={t('parcels.workflowStatus.confirmDescription')}
        confirmLabel={t('parcels.workflowStatus.confirmAction')}
        confirmVariant="danger"
        loading={revert.isPending}
        onCancel={() => {
          if (!revert.isPending) setConfirmOpen(false);
        }}
        onConfirm={() => revert.mutate(undefined)}
      />
    </>
  );
};
